import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { HorizontalScrollRow } from '../components/ModelCard';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Play, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API } from '../lib/api';

const AUTOPLAY_INTERVAL = 6000;

export function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [modelsByCategory, setModelsByCategory] = useState({});
  const [featuredModels, setFeaturedModels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Step 1: Load only the essentials (categories + featured) and unblock the page
    try {
      const [categoriesRes, featuredRes] = await Promise.all([
        axios.get(`${API}/categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/models?featured=true&limit=20`).catch(() => ({ data: [] }))
      ]);

      setCategories(categoriesRes.data);
      setFeaturedModels(featuredRes.data);
    } catch (error) {
      console.error('Failed to load essentials:', error);
    } finally {
      setLoading(false);
    }

    // Step 2: Load category models in the background, one at a time (non-blocking)
    try {
      const catsRes = await axios.get(`${API}/categories`);
      const categoryData = {};
      for (const cat of catsRes.data) {
        try {
          const res = await axios.get(`${API}/models?category=${cat.id}&limit=10`);
          categoryData[cat.id] = res.data;
          setModelsByCategory({ ...categoryData });
        } catch (e) {
          categoryData[cat.id] = [];
        }
      }
    } catch (error) {
      console.error('Failed to load category models:', error);
    }
  };

  // Auto-play
  useEffect(() => {
    if (isPaused || featuredModels.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredModels.length);
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [isPaused, featuredModels.length]);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredModels.length);
  };
  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredModels.length) % featuredModels.length);
  };
  const goTo = (idx) => setCurrentIndex(idx);

  const featuredModel = featuredModels[currentIndex] || null;
  const hasMultiple = featuredModels.length > 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-[#0066FF] text-xl">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <section
        data-testid="hero-banner"
        className="relative h-[70vh] min-h-[500px] mt-16 overflow-hidden group/hero"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={featuredModel?.id || 'fallback'}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            {featuredModel && featuredModel.images && featuredModel.images[0] ? (
              <img
                src={featuredModel.images[0]}
                alt={featuredModel?.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src="https://static.prod-images.emergentagent.com/jobs/af04cee4-1d95-42e7-9f81-e2167e421d48/images/a170a7499e3215ea93b41660c45252cf2919e2f324fef1d504e005de8e3ba321.png"
                alt="Featured"
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-16 pointer-events-none">
          <AnimatePresence mode="wait">
            <motion.div
              key={featuredModel?.id || 'fallback-content'}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="pointer-events-auto"
            >
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#00FF99]" />
                <span className="text-[#00FF99] text-sm font-bold uppercase tracking-wider">
                  {hasMultiple
                    ? t('hero.feature_badge_multi', { current: currentIndex + 1, total: featuredModels.length })
                    : t('hero.feature_badge_week')}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mb-4 max-w-3xl">
                {featuredModel?.title || t('hero.default_title')}
              </h1>
              <p className="text-lg text-zinc-300 mb-8 max-w-2xl line-clamp-2">
                {featuredModel?.description || t('hero.default_description')}
              </p>
              <div className="flex items-center space-x-4">
                {featuredModel && (
                  <button
                    data-testid="view-featured-button"
                    onClick={() => navigate(`/model/${featuredModel.id}`)}
                    className="flex items-center space-x-3 bg-white text-black px-8 py-3.5 rounded-lg font-bold hover:bg-zinc-200 transition-all shadow-lg"
                  >
                    <Play className="w-5 h-5" fill="currentColor" />
                    <span>{t('hero.view_details')}</span>
                  </button>
                )}
                {!user?.is_premium && (
                  <button
                    onClick={() => toast.info(t('common.premium_soon_toast'), { duration: 4000 })}
                    className="bg-[#1A1A1A] border border-[#27272A] hover:border-[#00FF99] hover:text-[#00FF99] text-white px-8 py-3.5 rounded-lg font-bold transition-all"
                  >
                    {t('hero.unlock_premium')}
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation arrows */}
        {hasMultiple && (
          <>
            <button
              data-testid="hero-prev-button"
              onClick={goPrev}
              aria-label={t('hero.prev_aria')}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white hover:bg-[#0066FF]/80 hover:border-[#0066FF] hover:scale-110 transition-all opacity-0 group-hover/hero:opacity-100 flex items-center justify-center"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              data-testid="hero-next-button"
              onClick={goNext}
              aria-label={t('hero.next_aria')}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white hover:bg-[#0066FF]/80 hover:border-[#0066FF] hover:scale-110 transition-all opacity-0 group-hover/hero:opacity-100 flex items-center justify-center"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {hasMultiple && (
          <div
            data-testid="hero-indicators"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center space-x-2"
          >
            {featuredModels.map((_, idx) => (
              <button
                key={idx}
                data-testid={`hero-indicator-${idx}`}
                onClick={() => goTo(idx)}
                aria-label={t('hero.indicator_aria', { n: idx + 1 })}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === currentIndex
                    ? 'w-10 bg-[#00FF99] shadow-[0_0_12px_rgba(0,255,153,0.6)]'
                    : 'w-2.5 bg-white/30 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
      </section>

      <main data-testid="models-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {categories.map((category) => (
          <HorizontalScrollRow
            key={category.id}
            title={category.name}
            category={category.id}
            models={modelsByCategory[category.id] || []}
            isPremiumUser={user?.is_premium}
          />
        ))}
      </main>

      <footer className="bg-[#0A0A0A] border-t border-white/5 py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-zinc-500 text-sm mb-2">
              {t('footer.disclaimer1')}
            </p>
            <p className="text-zinc-600 text-xs">
              {t('footer.disclaimer2')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}